import React, { useMemo } from 'react';
import { formatWholeNumberNaN } from '../../../../../../../core/utils/campaigns';
import EntityBreakdownCard from '../../../../../../shared/designSystem/components/EntityBreakdownCard';
import BreakdownRingChart from '../../../../../../shared/designSystem/components/BreakdownRingChart';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';

// Custom inline progress bar — replaces AntD's `<Progress/>` which kept
// pushing its percentage label outside narrow cells. Bar takes the
// flexible space; label sits on the right with a tabular-nums width so the
// numbers align across rows. Both pieces shrink within the cell, never
// overflow.
const ProgressCell = ({ percent, color }) => (
  <div className="campaign-sessions-v1__progress">
    <div className="campaign-sessions-v1__progress-track">
      <div
        className="campaign-sessions-v1__progress-fill"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%`, background: color }}
      />
    </div>
    <span className="campaign-sessions-v1__progress-label">
      {Number(percent).toFixed(1)}%
    </span>
  </div>
);

// =============================================================================
// SessionDeviceBreakdown (V1) — facelifted off legacy DonutChart + AntD Card
// + AntD Table chrome to the shared `EntityBreakdownCard` (table on left,
// ring on right). Preserves the desktop/mobile/tablet count semantics
// verbatim — only the chrome moves.
//
// Data input unchanged: `{ desktop, mobile, tablet }` counts. Normalized
// into a single array consumed by both the DataTable and the
// BreakdownRingChart so labels / colors stay in sync between the two.
// =============================================================================

const DEVICE_COLORS = {
  Desktop: '#4CAF50',
  Mobile: '#2196F3',
  Tablet: '#FF9800',
};

const SessionDeviceBreakdown = ({
  data = {
    desktop: 0,
    mobile: 0,
    tablet: 0,
  },
}) => {
  const totalSessions = useMemo(
    () => (data.desktop || 0) + (data.mobile || 0) + (data.tablet || 0),
    [data]
  );

  const rows = useMemo(() => {
    return [
      {
        name: 'Desktop',
        value: 'Desktop',
        color: DEVICE_COLORS.Desktop,
        sessions: data.desktop || 0,
        percentage: totalSessions > 0 ? ((data.desktop || 0) / totalSessions) * 100 : 0,
      },
      {
        name: 'Mobile',
        value: 'Mobile',
        color: DEVICE_COLORS.Mobile,
        sessions: data.mobile || 0,
        percentage: totalSessions > 0 ? ((data.mobile || 0) / totalSessions) * 100 : 0,
      },
      {
        name: 'Tablet',
        value: 'Tablet',
        color: DEVICE_COLORS.Tablet,
        sessions: data.tablet || 0,
        percentage: totalSessions > 0 ? ((data.tablet || 0) / totalSessions) * 100 : 0,
      },
    ];
  }, [data, totalSessions]);

  // Percentage widths so columns flex with the card width. Bar column gets
  // ~40% — enough room for a readable bar but no longer dominating the row.
  const columns = useMemo(
    () => [
      {
        id: 'device',
        accessorKey: 'name',
        header: 'Device',
        sortable: false,
        meta: { align: 'left', width: '32%' },
        cell: ({ row, getValue }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 9999,
                background: row.original.color,
                marginRight: 8,
                flexShrink: 0,
              }}
            />
            {getValue()}
          </span>
        ),
      },
      {
        id: 'sessions',
        accessorKey: 'sessions',
        header: 'Sessions',
        sortable: false,
        meta: { align: 'right', width: '28%' },
        cell: ({ getValue }) => formatWholeNumberNaN(getValue()),
      },
      {
        id: 'percentage',
        accessorKey: 'percentage',
        header: 'Percentage',
        sortable: false,
        meta: { align: 'left' },
        cell: ({ row, getValue }) => (
          <ProgressCell percent={Number(getValue())} color={row.original.color} />
        ),
      },
    ],
    []
  );

  return (
    <EntityBreakdownCard
      title="Device Breakdown"
      table={
        <DataTable
          data={rows}
          columns={columns}
          pageSize={3}
          pageSizeOptions={[3]}
          emptyState="No device data yet."
          className="campaign-sessions-v1__device-table"
          summaryRow={{
            labelColumnId: 'device',
            label: 'Total',
            cells: {
              sessions: formatWholeNumberNaN(totalSessions),
              percentage: '100%',
            },
          }}
        />
      }
      breakdownChart={
        <BreakdownRingChart
          data={rows}
          dimension="sessions"
          height={320}
          dimensionLabels={{ sessions: 'Sessions' }}
        />
      }
    />
  );
};

export default SessionDeviceBreakdown;
